import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { getArticleBySlug } from "@/lib/constants/blog-data";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { Calendar } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { fetchPostBySlug, renderMarkdown, type CmsPost } from "@/lib/blogCms";
import { supabase } from "@/integrations/supabase/client";

const BASE = "https://sorvaine.com";

function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem('sv_blog_session');
    if (!id) {
      id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('sv_blog_session', id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

async function trackBlogView(slug: string, postId: string | null) {
  try {
    const dedupeKey = `sv_blog_view_${slug}`;
    const last = sessionStorage.getItem(dedupeKey);
    if (last && Date.now() - Number(last) < 30 * 60 * 1000) return;
    sessionStorage.setItem(dedupeKey, String(Date.now()));
    await supabase.from('blog_post_views').insert({
      slug,
      post_id: postId,
      session_id: getOrCreateSessionId(),
      referrer: document.referrer || null,
      user_agent: navigator.userAgent.slice(0, 500),
    });
  } catch (e) {
    // Silent — analytics must never break the page
  }
}

export default function ResourceArticle() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const [cmsPost, setCmsPost] = useState<CmsPost | null | undefined>(undefined);
  useEffect(() => {
    if (article || !slug) { setCmsPost(null); return; }
    fetchPostBySlug(slug).then(setCmsPost);
  }, [slug, article]);

  // Track pageview for hardcoded articles
  useEffect(() => {
    if (article && slug) trackBlogView(slug, null);
  }, [article, slug]);

  // Track pageview for CMS posts
  useEffect(() => {
    if (cmsPost && cmsPost.slug) trackBlogView(cmsPost.slug, cmsPost.id);
  }, [cmsPost]);


  // Hardcoded article path
  if (article) {
    return (
      <ContentPageLayout
        title={article.seoTitle}
        description={article.metaDescription}
        canonical={`${BASE}/resources/${article.slug}`}
        breadcrumbs={[
          { name: "Home", url: BASE },
          { name: "Resources", url: `${BASE}/resources` },
          { name: article.title, url: `${BASE}/resources/${article.slug}` },
        ]}
      >
        <SchemaMarkup
          type="article"
          title={article.seoTitle}
          description={article.metaDescription}
          datePublished={article.datePublished}
          url={`${BASE}/resources/${article.slug}`}
        />
        {article.faq && article.faq.length > 0 && (
          <SchemaMarkup type="faq" items={article.faq} />
        )}

        <header className="border-b border-gray-100 pb-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-primary">{article.category}</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <Calendar className="w-3 h-3" />
              {new Date(article.datePublished).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>

          <h1 className="text-4xl md:text-[44px] font-bold tracking-tight text-gray-900 leading-[1.1]">
            {article.title}
          </h1>
          <p className="mt-5 text-[18px] text-gray-600 leading-relaxed max-w-[680px]">{article.excerpt}</p>
        </header>

        <article className="mt-12 space-y-12">
          {article.sections.map((section, i) => (
            <section key={i}>
              {section.level === 'h2' ? (
                <h2 className="text-[26px] font-semibold tracking-tight text-gray-900 leading-tight">{section.heading}</h2>
              ) : (
                <h3 className="text-[20px] font-semibold tracking-tight text-gray-900 leading-tight">{section.heading}</h3>
              )}
              <div
                className="mt-4 prose prose-base max-w-none text-gray-600 leading-[1.75] prose-p:text-gray-600 prose-p:leading-[1.75] prose-p:my-4 prose-strong:text-gray-900 prose-strong:font-semibold prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline prose-ul:my-4 prose-ul:space-y-2 prose-li:text-gray-600 prose-li:marker:text-primary/60 prose-ol:my-4 prose-ol:space-y-2 prose-headings:text-gray-900"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(renderMarkdown(section.content))
                }}
              />
            </section>
          ))}
        </article>

        {article.faq && article.faq.length > 0 && (
          <section className="mt-16 pt-12 border-t border-gray-100">
            <h2 className="text-[26px] font-semibold tracking-tight text-gray-900">Frequently asked questions</h2>
            <div className="mt-8 space-y-3">
              {article.faq.map((item, i) => (
                <div key={i} className="rounded-xl border border-gray-200 bg-white p-6 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
                  <h3 className="text-[15px] font-semibold text-gray-900">{item.question}</h3>
                  <p className="mt-2 text-[15px] text-gray-600 leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-16 pt-12 border-t border-gray-100">
          <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">Keep reading</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {article.relatedSlugs.map((rs) => {
              const related = getArticleBySlug(rs);
              if (!related) return null;
              return (
                <Link
                  key={rs}
                  to={`/resources/${rs}`}
                  className="group block rounded-xl border border-gray-200 bg-white p-5 hover:border-primary/40 hover:shadow-[0_4px_16px_-4px_rgba(0,0,0,0.06)] transition-all"
                >
                  <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-primary/80">{related.category}</span>
                  <h3 className="mt-2 text-[15px] font-semibold text-gray-900 leading-snug group-hover:text-primary transition-colors">{related.title}</h3>
                </Link>
              );
            })}
          </div>
        </section>
      </ContentPageLayout>
    );
  }

  // CMS path
  if (cmsPost === undefined) {
    return (
      <ContentPageLayout title="Loading…" description="" canonical={`${BASE}/resources/${slug}`} breadcrumbs={[{ name: "Home", url: BASE }, { name: "Resources", url: `${BASE}/resources` }]}>
        <div className="h-40" />
      </ContentPageLayout>
    );
  }
  if (cmsPost === null) {
    return <Navigate to="/resources" replace />;
  }

  const seoTitle = cmsPost.seo_title || `${cmsPost.title} | Sorvaine`;
  const metaDesc = cmsPost.meta_description || cmsPost.excerpt;
  const dateStr = cmsPost.published_at
    ? new Date(cmsPost.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <ContentPageLayout
      title={seoTitle}
      description={metaDesc}
      canonical={`${BASE}/resources/${cmsPost.slug}`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "Resources", url: `${BASE}/resources` },
        { name: cmsPost.title, url: `${BASE}/resources/${cmsPost.slug}` },
      ]}
    >
      <SchemaMarkup
        type="article"
        title={seoTitle}
        description={metaDesc}
        datePublished={cmsPost.published_at || new Date().toISOString()}
        url={`${BASE}/resources/${cmsPost.slug}`}
      />

      <header className="border-b border-gray-100 pb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-primary">{cmsPost.category}</span>
          {dateStr && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span className="flex items-center gap-1.5 text-[12px] text-gray-500">
                <Calendar className="w-3 h-3" />
                {dateStr}
              </span>
            </>
          )}
          {cmsPost.read_time && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span className="text-[12px] text-gray-500">{cmsPost.read_time} read</span>
            </>
          )}
        </div>

        <h1 className="text-4xl md:text-[44px] font-bold tracking-tight text-gray-900 leading-[1.1]">
          {cmsPost.title}
        </h1>
        {cmsPost.excerpt && <p className="mt-5 text-[18px] text-gray-600 leading-relaxed max-w-[680px]">{cmsPost.excerpt}</p>}
      </header>

      {cmsPost.cover_image_url && (
        <img src={cmsPost.cover_image_url} alt={cmsPost.title} className="mt-10 w-full h-auto rounded-xl" />
      )}

      <article
        className="mt-12 prose prose-base max-w-none text-gray-600 leading-[1.75] prose-p:text-gray-600 prose-p:leading-[1.75] prose-p:my-5 prose-strong:text-gray-900 prose-strong:font-semibold prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline prose-ul:my-5 prose-ul:space-y-2 prose-li:text-gray-600 prose-li:marker:text-primary/60 prose-ol:my-5 prose-ol:space-y-2 prose-headings:text-gray-900 prose-headings:tracking-tight prose-h2:text-[26px] prose-h2:font-semibold prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-[20px] prose-h3:font-semibold prose-h3:mt-10 prose-h3:mb-3 prose-img:w-full prose-img:h-auto prose-img:rounded-xl"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMarkdown(cmsPost.body_markdown)) }}
      />
    </ContentPageLayout>
  );
}
